import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { ServiceService } from '../service.service';

export const usePatchService = (
	onSuccess?: (service: Services.Service) => void,
) => {
	const queryClient = useQueryClient();

	const patchServiceMutation = useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Partial<Services.Create>;
		}) => ServiceService.patch(id, body),
		onSuccess: (service) => {
			queryClient.invalidateQueries({ queryKey: ['services'] });
			toast.success('Serviço atualizado com sucesso!');
			if (onSuccess) onSuccess(service);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do serviço'),
			);
		},
	});

	return patchServiceMutation;
};

export const useRemoveService = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => ServiceService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['services'] });
			toast.success('Recurso removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover o recurso'));
		},
	});
};

export const useCreateService = (
	onSuccess?: (service: Services.Service) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Services.Create) => ServiceService.post(body), // Presume-se que existe ServiceService.post(body)
		onSuccess: (service: Services.Service) => {
			queryClient.invalidateQueries({ queryKey: ['services'] });
			toast.success('Serviço criado com sucesso!');
			if (onSuccess) onSuccess(service);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar o serviço'));
		},
	});
};

export const useToggleServiceStatus = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, active }: { id: string; active: boolean }) =>
			ServiceService.toggleStatus(id, active), // Usa o novo método
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['services'] });
			toast.success('Status do serviço atualizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao alterar o status do serviço'),
			);
		},
	});
};

/** Reordena a exibição dos serviços para o usuário (agendamento/home). */
export const useReorderServicesDisplay = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Services.ReorderDisplay) =>
			ServiceService.reorderDisplay(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['services'] });
			toast.success('Ordem de exibição salva!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao salvar a ordem de exibição!'),
			);
		},
	});
};
